export type AnalyticsConsent = 'granted' | 'denied';

/** Cookie banner body. */
export const ANALYTICS_CONSENT_DETAIL =
  'We use optional analytics cookies to understand how the app is used. They record page views and actions with account and company IDs only, not your name, email, or password. You can change this later in Settings.';

/** Settings → Usage analytics tooltip. */
export const ANALYTICS_CONSENT_SUMMARY =
  'Optional cookies that help us understand how the app is used. They send account and company IDs only, not your name or email.';

const STORAGE_PREFIX = 'hf_ga_consent';
const listeners = new Set<(value: AnalyticsConsent | null) => void>();

function isConsent(value: string | null): value is AnalyticsConsent {
  return value === 'granted' || value === 'denied';
}

export function consentStorageKey(sub: string): string {
  return `${STORAGE_PREFIX}:${sub.trim()}`;
}

function dropLegacyUnscoped(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_PREFIX);
}

function readStored(storage: Storage | undefined, key: string): AnalyticsConsent | null {
  if (!storage) return null;
  const raw = storage.getItem(key);
  return isConsent(raw) ? raw : null;
}

/** Per-user localStorage when `sub` is set; otherwise this tab’s session (login page). Never the legacy unscoped key. */
export function readConsent(sub?: string | null): AnalyticsConsent | null {
  dropLegacyUnscoped();
  if (sub) {
    if (typeof localStorage === 'undefined') return null;
    return readStored(localStorage, consentStorageKey(sub));
  }
  if (typeof sessionStorage === 'undefined') return null;
  return readStored(sessionStorage, STORAGE_PREFIX);
}

export function writeConsent(value: AnalyticsConsent | null, sub?: string | null): void {
  dropLegacyUnscoped();
  if (sub) {
    if (typeof localStorage === 'undefined') return;
    const key = consentStorageKey(sub);
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } else if (typeof sessionStorage !== 'undefined') {
    if (value) sessionStorage.setItem(STORAGE_PREFIX, value);
    else sessionStorage.removeItem(STORAGE_PREFIX);
  }
  listeners.forEach((fn) => fn(value));
}

/** Login-page / leftover session only. Per-user keys stay so the same account can restore after settings load. */
export function clearSessionConsent(): void {
  dropLegacyUnscoped();
  if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(STORAGE_PREFIX);
  listeners.forEach((fn) => fn(null));
}

export function subscribeConsent(listener: (value: AnalyticsConsent | null) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
